/* NORA global behaviour: announcement rotator, header state, drawer close, scroll reveal. */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionOff = () =>
    reducedMotion.matches || document.body.classList.contains('nora-motion-off') || Boolean(window.Shopify && Shopify.designMode);

  /* Announcement rotator (mobile/tablet only; desktop shows all items inline). */
  if (!customElements.get('nora-rotator')) {
    class NoraRotator extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.nora-announcement__item'));
        this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
        this.interval = Math.max(2000, parseInt(this.dataset.interval, 10) || 4000);
        this.paused = false;
        this.desktop = window.matchMedia('(min-width: 990px)');

        this.onPause = () => { this.paused = true; };
        this.onResume = () => { this.paused = false; };
        this.onMediaChange = () => this.restart();

        this.addEventListener('mouseenter', this.onPause);
        this.addEventListener('mouseleave', this.onResume);
        this.addEventListener('focusin', this.onPause);
        this.addEventListener('focusout', this.onResume);
        this.desktop.addEventListener('change', this.onMediaChange);
        reducedMotion.addEventListener('change', this.onMediaChange);
        this.addEventListener('shopify:block:select', (event) => this.show(this.items.indexOf(event.target), true));
        this.addEventListener('shopify:block:deselect', this.onResume);

        this.restart();
      }

      disconnectedCallback() {
        this.stop();
        this.desktop.removeEventListener('change', this.onMediaChange);
        reducedMotion.removeEventListener('change', this.onMediaChange);
      }

      restart() {
        this.stop();
        if (this.items.length < 2 || this.desktop.matches || reducedMotion.matches) return;
        this.timer = window.setInterval(() => {
          if (!this.paused && !document.hidden) this.show(this.index + 1);
        }, this.interval);
      }

      stop() {
        if (this.timer) window.clearInterval(this.timer);
        this.timer = null;
      }

      show(index, pause = false) {
        if (index < 0 || !this.items.length) return;
        this.index = index % this.items.length;
        this.items.forEach((item, i) => item.classList.toggle('is-active', i === this.index));
        if (pause) this.paused = true;
      }
    }
    customElements.define('nora-rotator', NoraRotator);
  }

  /* Header: compact/solid state once the page scrolls. */
  let headerTicking = false;
  const updateHeader = () => {
    headerTicking = false;
    const scrolled = window.scrollY > 8;
    document.querySelectorAll('[data-nora-header]').forEach((header) => {
      header.classList.toggle('is-scrolled', scrolled);
    });
  };
  window.addEventListener(
    'scroll',
    () => {
      if (headerTicking) return;
      headerTicking = true;
      window.requestAnimationFrame(updateHeader);
    },
    { passive: true }
  );

  /* Drawer close button inside Atlas header-drawer. */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-nora-drawer-close]');
    if (!button) return;
    const drawer = button.closest('header-drawer');
    const summary = drawer && drawer.querySelector('summary');
    if (drawer && typeof drawer.closeMenuDrawer === 'function') {
      drawer.closeMenuDrawer(event, summary);
    } else if (drawer) {
      const details = drawer.querySelector('details');
      if (details) details.removeAttribute('open');
    }
  });

  /* Scroll reveal. Content is visible without JS (styles key off html.js). */
  let revealObserver = null;
  const revealAll = (root) => {
    root.querySelectorAll('[data-nora-reveal]').forEach((el) => el.classList.add('is-revealed'));
  };
  const initReveal = (root = document) => {
    if (motionOff() || !('IntersectionObserver' in window)) {
      revealAll(root);
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    root.querySelectorAll('[data-nora-reveal]:not(.is-revealed)').forEach((el) => revealObserver.observe(el));
  };

  const init = (root = document) => {
    updateHeader();
    initReveal(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  /* Failsafe: never leave content hidden if something prevents the observer from firing. */
  window.addEventListener('load', () => {
    window.setTimeout(() => {
      document.querySelectorAll('[data-nora-reveal]:not(.is-revealed)').forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) el.classList.add('is-revealed');
      });
    }, 1200);
  });

  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) revealAll(document);
  });

  /* Theme editor */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    event.target.querySelectorAll('[data-nora-reveal]').forEach((el) => el.classList.add('is-revealed'));
    if (event.target.matches('[data-nora-reveal]')) event.target.classList.add('is-revealed');
  });
})();
